// El orden de la vista "Todas" de plantillas: las más usadas primero
// (ADR-2026-10-29-todas-las-plantillas-por-uso).
//
// `uso` es cuántas empresas usaron cada plantilla en los últimos 60 días (la función
// `uso_de_plantillas` de la base). Un empate —incluidas todas las que nadie usó— conserva el
// orden que traía la lista, que es el editorial (categorías y `orden` cargados a mano): así una
// plantilla nueva no cae al fondo por azar. Las de `alFinal` (las poses de Fondo blanco, que no
// son plantillas y no tienen uso) van detrás de todas, en su orden.

import { supabase } from '../../lib/supabase';
import { withTimeout } from '../../lib/withTimeout';

export function ordenarPorUso<T extends { id: string }>(
  presets: readonly T[],
  uso: ReadonlyMap<string, number>,
  alFinal: (p: T) => boolean,
): T[] {
  const adelante = presets.filter((p) => !alFinal(p));
  const atras = presets.filter(alFinal);
  if (uso.size === 0) return [...adelante, ...atras];
  const conPosicion = adelante.map((p, i) => ({ p, i, n: uso.get(p.id) ?? 0 }));
  conPosicion.sort((a, b) => b.n - a.n || a.i - b.i);
  return [...conPosicion.map((x) => x.p), ...atras];
}

/**
 * Cuánto espera la grilla el dato de uso, contado desde que se pide (en paralelo con el
 * catálogo). En producción la función tarda ~200 ms; el tope existe para que un pedido trabado
 * no deje la grilla sin dibujar (ADR-2026-10-29-uso-de-plantillas-con-tope).
 */
export const TOPE_USO_DE_PLANTILLAS_MS = 3000;

/**
 * Empresas por plantilla en los últimos 60 días. Si la función todavía no existe en la base,
 * falla o no responde en `TOPE_USO_DE_PLANTILLAS_MS`, devuelve un mapa vacío: la grilla queda en
 * el orden editorial de siempre.
 */
export async function fetchUsoDePlantillas(): Promise<Map<string, number>> {
  try {
    const { data, error } = await withTimeout(
      Promise.resolve(supabase.rpc('uso_de_plantillas', { p_dias: 60 })),
      TOPE_USO_DE_PLANTILLAS_MS,
      'uso_de_plantillas',
    );
    if (error || !Array.isArray(data)) return new Map();
    const uso = new Map<string, number>();
    for (const fila of data as { template_id: string | null; empresas: number | null }[]) {
      if (fila.template_id) uso.set(fila.template_id, Number(fila.empresas) || 0);
    }
    return uso;
  } catch {
    return new Map();
  }
}
